<template>
  <div
    ref="resource-container"
    class="max-w-full overflow-x-hidden overflow-y-scroll flex flex-col lg:flex-1 min-h-[100svh-73px] lg:min-h-screen"
    :class="{ 'lg:overflow-y-visible': isGPCPage() }"
  >
    <div
      class="px-6 pt-[24px] pb-6 bg-grey-10 overflow-visible md:px-[56px]"
      :class="{
        '!pt-[68px]': isIos,
        '!pt-[max(24px,env(safe-area-inset-top))]': isAndroid,
        'lg:pt-[56px]': isGPCPage() || isGICPage(),
      }"
    >
      <div class="flex justify-between items-center">
        <div>
          <h1 class="page-title">
            {{ $t('copy.resourcesPage.title') }}
          </h1>
        </div>
        <div>
          <!-- CSS mask instead of ion-icon: ion-icon loads its SVG via fetch, which the
               CapacitorHttp patch breaks on iOS native (icon rendered blank). -->
          <div
            v-if="isGICPage()"
            class="flex items-center space-x-2 text-orange"
            @click="goToMyList"
          >
            <span
              class="bookmarks-icon-mask w-[28px] h-[28px]"
              :style="{ '--bookmarks-icon': 'url(/assets/icons/bookmarks-outline.svg)' }"
              role="img"
              aria-label=""
            ></span>
            <span class="text-lg text-orange font-semibold font-aktiv-grotex-trial">
              {{ myListHeader }}
            </span>
          </div>
          <div
            v-else-if="!isGPCRPage()"
            class="flex items-center space-x-2 text-lightblue"
            @click="goToMyList"
          >
            <span
              class="bookmarks-icon-mask w-[28px] h-[28px]"
              :style="{ '--bookmarks-icon': 'url(/assets/icons/bookmarks-outline.svg)' }"
              role="img"
              aria-label=""
            ></span>
            <span class="text-lg text-lightblue font-semibold font-aktiv-grotex-trial">
              {{ myListHeader }}
            </span>
          </div>
        </div>
      </div>

      <!-- Filter pills stay on one line and scroll horizontally; the negative margins let them
           bleed to the screen edges instead of wrapping when there are many GIC conferences. -->
      <div
        v-if="isGICPage()"
        class="flex flex-row items-center gap-2 flex-nowrap overflow-x-auto no-scrollbar mt-4 -mx-6 px-6 md:-mx-[56px] md:px-[56px]"
      >
        <div
          v-for="button in buttons"
          :key="button.displayText"
          class="shrink-0 border-2 border-orange px-3 py-[.375rem] text-center font-semibold font-aktiv-grotex-trial whitespace-nowrap text-orange rounded-full transition duration-75 ease-linear cursor-pointer hover:bg-orange hover:text-white"
          :class="isHighlight(button) ? '!bg-orange !text-white' : ''"
          :data-event-tracking="'filter-' + button.displayText.toLowerCase()"
          :data-test-id="'filter-' + button.displayText.toLowerCase()"
          @click="handleClick(button)"
        >
          {{ button.translatedText }}
        </div>
      </div>
      <div v-else-if="isSCS()">
        <!-- Hide the whole All/Sessions filter row when there are no sessions to filter. -->
        <div v-if="hasSessions" class="flex flex-row items-center gap-2 mt-4">
          <div
            v-for="page in pagesSCS"
            :key="page.displayText"
            class="border-2 border-lightblue px-3 py-[.375rem] text-center font-semibold font-aktiv-grotex-trial whitespace-nowrap text-lightblue rounded-full transition duration-75 ease-linear cursor-pointer hover:bg-lightblue hover:text-white"
            :class="[
              (page.displayText === 'Sessions' && filteredSessions.length > 0) ||
              (page.displayText === 'All' && filteredSessions.length === 0)
                ? 'bg-lightblue text-white'
                : '',
            ]"
            @click="handleClick(page)"
          >
            <ion-icon
              v-if="page.icon"
              class="rotate-90 pl-1 text-base"
              :icon="optionsIcon"
            ></ion-icon>
            {{ page.translatedText }}
          </div>
        </div>
      </div>
      <div v-else class="flex flex-wrap gap-2">
        <router-link
          v-for="page in pagesGC"
          :key="page.displayText"
          :to="page.path"
          class="border-2 border-lightblue px-3 py-[.375rem] text-center font-semibold font-aktiv-grotex-trial whitespace-nowrap rounded-full transition duration-75 ease-linear hover:bg-white"
          :class="{
            'bg-lightblue text-white pointer-events-none': $route.path === page.path,
            'text-lightblue': $route.path !== page.path,
          }"
          :data-event-tracking="gcTabDET(page)"
          :data-test-id="'gensapp-resources-gc-tabs-' + page.displayText"
          @click="logGCEvent(page)"
        >
          {{ page.displayText }}
        </router-link>
      </div>
    </div>
    <div class="bg-grey-5 pt-10 pb-8 relative w-full flex-1 md:px-[56px]">
      <slot></slot>
    </div>
    <!-- pb-28 below lg clears the floating bottom nav pill while keeping the grey background -->
    <div class="px-[24px] md:pl-[56px] flex flex-col items-center pb-28 lg:pb-20 bg-[#F8F9F9]">
      <div
        class="font-aktiv-grotesk-trial text-center font-semibold text-[20px] text-grey-100 sm:mr-2"
      >
        {{ isGPCRPage() ? $t('copy.resourcesPage.backToTopSection.otherContentText') : '' }}
        <div v-if="!isGPCRPage()">
          <span>Looking for a specific resource?</span>
          <br />
          <span>Try the Search Function</span>
        </div>
      </div>
      <div
        class="flex items-center cursor-pointer hover:opacity-90"
        data-test-id="gensapp-resourcespage-backtotop"
        @click="scrollToTop"
      >
        <p
          :class="[
            'font-aktiv-grotesk-trial font-semibold text-[20px] mr-2 hover:underline',
            isGICPage() ? 'text-orange' : 'text-lightblue',
          ]"
        >
          {{ $t('copy.resourcesPage.backToTopSection.backToTop') }}
        </p>
        <img
          class="w-[12px]"
          :src="
            isGICPage()
              ? require('../../../public/assets/images/chevron-up-orange.svg')
              : require('../../../public/assets/images/chevron-up-blue.svg')
          "
          alt=""
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import platformMixin from '@/mixins/platformMixin';
import { FirebaseAnalytics } from '@/utils/firebase-analytics-wrapper';
import { options } from 'ionicons/icons';
import { isGPCRPage, isGICPage } from '@/utils/conference-type';

export default {
  name: 'ResourcesContainer',
  mixins: [platformMixin],
  props: {
    showMoreResources: {
      type: Boolean,
      default: false,
    },
    showSelectedSessions: {
      type: String,
      default: 'All',
    },
  },
  data() {
    return {
      optionsIcon: options,
      allPagesGC: [
        {
          path: '/resources/sessions',
          displayText: 'Sessions',
        },
        {
          path: '/resources/book',
          displayText: 'Book',
        },
        {
          path: '/resources/more-resources',
          displayText: 'More Resources',
        },
      ],
    };
  },
  mounted() {},
  computed: {
    ...mapState('cms', ['loading']),
    ...mapState('filter', [
      'showFilterResults',
      'filteredSessions',
      'filteredItems',
      'selectedFilter',
      'filterTypeScheduleMapping',
      'schedule',
    ]),
    ...mapGetters('profile', ['type']),
    ...mapGetters('filter', ['myListHeader']),
    buttons() {
      // For GIC pages, generate buttons dynamically based on available gicConferenceId values
      if (this.isGICPage()) {
        const buttons = [
          {
            displayText: 'All',
            translatedText: this.$t('copy.resourcesPage.filterSection.all'),
            type: null,
            typeId: null,
            path: '/gic/resources/sessions', // Added for consistency (used for analytics only, not navigation)
          },
        ];

        // Get unique gicConferenceId values from filterTypeScheduleMapping.
        // Sorted descending so the most recent GIC conference sits first in the pill slider.
        const gicConferenceIds = Object.keys(this.filterTypeScheduleMapping || {})
          .map(id => parseInt(id))
          .sort((a, b) => b - a);

        // Create a button for each GIC conference
        gicConferenceIds.forEach(gicConferenceId => {
          buttons.push({
            displayText: `GIC ${gicConferenceId}`,
            translatedText: this.$t(
              `copy.resourcesPage.filterSection.gic-${gicConferenceId}`,
              `GIC ${gicConferenceId}`,
            ),
            type: 'Sessions',
            typeId: gicConferenceId,
            path: '/gic/resources/sessions', // Added for consistency (used for analytics only, not navigation)
          });
        });

        return buttons;
      }

      // For non-GIC pages, return default buttons
      const defaultButtons = [
        {
          displayText: 'All',
          translatedText: this.$t('copy.resourcesPage.filterSection.all'),
          type: null,
          typeId: null,
        },
        {
          displayText: 'Sessions',
          translatedText: this.$t('copy.resourcesPage.filterSection.sessions'),
          type: 'session',
          typeId: null,
        },
      ];
      return defaultButtons;
    },
    // True when the sessions API (/cms/schedules/session/all → filter/schedule) returned
    // at least one session. Used to hide the session filter pills when there are none.
    hasSessions() {
      return Array.isArray(this.schedule) && this.schedule.length > 0;
    },
    pagesGC() {
      if (!this.showMoreResources) return this.allPagesGC.slice(0, 2);

      return this.allPagesGC;
    },
    pagesSCS() {
      if (!this.showMoreResources) return this.allPagesSCS.slice(0, 2);

      return this.allPagesSCS;
    },
    allPagesSCS() {
      return [
        {
          path: '/resources',
          displayText: 'All',
          translatedText: this.$t('copy.resourcesPage.filterSection.all'),
          icon: '',
        },
        {
          path: '/resources/sessions',
          displayText: 'Sessions',
          translatedText: this.$t('copy.resourcesPage.filterSection.sessions'),
          icon: 'optionsIcon',
        },
      ];
    },
  },
  methods: {
    scsTabDET(page) {
      const pathName = page.path;
      const pos = pathName.lastIndexOf('/');
      const result = 'gensapp-resources-scs-tabs-' + pathName.substring(pos + 1);
      return result;
    },
    gcTabDET(page) {
      const pathName = page.path;
      const pos = pathName.lastIndexOf('/');
      const result = 'gensapp-resources-gc-tabs-' + pathName.substring(pos + 1);
      return result;
    },
    async openFilterModal(page) {
      const showFilterModal = true;
      // For GIC pages, selectedFilter is already set in handleClick
      if (this.isGICPage()) {
        await this.$store.dispatch('filter/updateShowFilterModal', {
          showFilterModal,
        });
      } else {
        // Existing system expects filterType
        const filterType = page.displayText || page.translatedText || 'Sessions';
        await this.$store.dispatch('filter/updateShowFilterModal', {
          showFilterModal,
          filterType,
        });
      }

      // Analytics tracking - all buttons now have paths for consistency
      // Note: GIC and SCS buttons use paths for analytics only (they're filter buttons, not navigation)
      // GC buttons use paths for both navigation and analytics
      if (page.path) {
        const pathName = page.path;
        const pos = pathName.lastIndexOf('/');
        // GIC buttons use different naming convention (filter buttons, not navigation)
        const result = this.isGICPage()
          ? `gensapp-resources-gic-tabs-${page.typeId || 'all'}`
          : `gensapp-resources-scs-tabs-${pathName.substring(pos + 1)}`;
        FirebaseAnalytics.logEvent({
          name: 'click_button',
          params: {
            button_name: result,
            page: 'resources',
          },
        });
      }
    },
    logGCEvent(page) {
      const pathName = page.path;
      const pos = pathName.lastIndexOf('/');
      const result = 'gensapp-resources-gc-tabs-' + pathName.substring(pos + 1);
      FirebaseAnalytics.logEvent({
        name: 'click_button',
        params: {
          button_name: result,
          page: 'resources',
        },
      });
    },
    scrollToTop() {
      const MAP = {
        '/resources/book': 'contentResourceBook',
        '/resources/sessions': 'contentResourceSessions',
        '/resources/learning-communities': 'contentResourceLearningCommunity',
        '/resources/more-resources': 'contentResourceMoreResources',
        '/gpcr/resources/sessions': 'gpcrContentResourceSessions',
        '/gic/resources/sessions': 'gicContentResourceSessions',
      };

      const elem = document.getElementById(MAP[this.$route.path]);
      console.log(elem);
      elem.scrollToTop(1000);
    },
    isQuotes() {
      return this.$route.path === '/resources/quotes';
    },
    isTools() {
      return this.$route.path === '/resources/tools';
    },
    isSCS() {
      if (this.type === 'SCS') {
        return true;
      } else return false;
    },
    getItemClass(displayText) {
      // const baseClass = 'px-3 py-1 rounded';
      const highlightClass = 'bg-lightblue text-white';

      if (displayText === 'All') {
        return !this.showFilterResults ? `${highlightClass}` : '';
      } else {
        return this.showFilterResults ? `${highlightClass}` : '';
      }
    },
    async handleClick(button) {
      // For GIC pages, use the new filter system
      if (this.isGICPage()) {
        await this.$store.dispatch('filter/setClickedFilter', button);
        if (button.displayText === 'All') {
          this.resetFilter();
        } else {
          // If switching to a different filter button, clear previous selections
          if (this.selectedFilter && this.selectedFilter.typeId !== button.typeId) {
            await this.$store.dispatch('filter/updateFilteredItems', { filteredItems: [] });
          }
          // Set selectedFilter before opening modal
          await this.$store.dispatch('filter/setSelectedFilter', {
            type: button.type,
            typeId: button.typeId,
          });
          this.openFilterModal(button);
        }
      } else {
        // For existing pages (/resources/sessions, /gpcr/resources/sessions), use old system
        // This handles SCS filter buttons
        if (button.displayText === 'All') {
          this.resetFilter();
        } else {
          this.openFilterModal(button);
        }
      }
    },
    async resetFilter() {
      // For GIC pages, use the new filter system
      if (this.isGICPage()) {
        await this.$store.dispatch('filter/updateFilteredItems', {
          filteredItems: [],
        });
        await this.$store.dispatch('filter/setSelectedFilter', {});
        await this.$store.commit('filter/clearFilteredResources');
      }
      // Always clear the old filter system for backward compatibility
      // This ensures existing pages (/resources/sessions, /gpcr/resources/sessions) continue to work
      await this.$store.dispatch('filter/updateFilteredValues', {
        filteredSessions: [],
        filteredTopics: [],
      });
      await this.$store.dispatch('filter/updateShowFilterResults', false);
    },
    isGPCRPage() {
      return isGPCRPage();
    },
    isGICPage() {
      return isGICPage();
    },
    isGPCPage() {
      return !isGPCRPage() && !isGICPage();
    },
    isHighlight(button) {
      if (button.displayText === 'All' && this.filteredItems.length === 0) return true;

      // If no items are filtered, only "All" should be highlighted
      if (this.filteredItems.length === 0) return false;

      if (!this.selectedFilter || Object.keys(this.selectedFilter).length === 0) return false;

      const buttonFilterKey = `${button.type}-${button.typeId}`;
      const selectedFilterKey = `${this.selectedFilter.type}-${this.selectedFilter.typeId}`;
      return selectedFilterKey === buttonFilterKey;
    },
    goToMyList() {
      const currentPath = this.$route.path;
      if (currentPath.includes('/gic/')) {
        this.$router.push('/gic/resources/sessions/mylist');
      } else {
        this.$router.push('/resources/sessions/mylist');
      }
    },
    goToSearch() {
      const currentPath = this.$route.path;
      if (currentPath.includes('/gic/')) {
        this.$router.push('/gic/resources/sessions/search');
      } else {
        this.$router.push('/resources/sessions/search');
      }
    },
  },
};
</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.bookmarks-icon-mask {
  display: block;
  background-color: currentColor;
  -webkit-mask-image: var(--bookmarks-icon);
  mask-image: var(--bookmarks-icon);
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-size: contain;
  mask-size: contain;
}
</style>
